import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, Download, Check, Users, GraduationCap, Briefcase, Video, Globe } from 'lucide-react';
import { useCasePages } from '../data/knowledgeHub';
import { Helmet } from 'react-helmet-async';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.typeit4u.keyboard";

const iconMap = {
  'for-students': GraduationCap,
  'for-business': Briefcase,
  'for-content-creators': Video,
  'for-tamil-users': Globe
};

const UseCaseArticle = () => {
  const { slug } = useParams();
  const useCase = useCasePages.find(u => u.slug === slug);

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  if (!useCase) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 container text-center">
          <h1 className="text-3xl font-bold mb-4">Use Case Not Found</h1>
          <Link to="/use-case" className="text-orange-600 hover:underline">View All Use Cases</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = iconMap[useCase.id] || Users;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{useCase.title} | TypeIt4U</title>
        <meta name="description" content={useCase.description} />
        <meta name="keywords" content={useCase.keywords?.join(', ')} />
        <link rel="canonical" href={`https://typeit4u.com/use-case/${useCase.slug}`} />
      </Helmet>

      <Navbar />
      
      {/* Breadcrumb */}
      <section className="pt-28 pb-4 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <Link to="/use-case" className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            All Use Cases
          </Link>
        </div>
      </section>

      {/* Hero */}
      <section className="pb-12 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="text-center"
          >
            <div 
              className="w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6"
              style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)' }}
            >
              <Icon className="w-12 h-12 text-white" />
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              {useCase.title}
            </h1>
            <p className="text-lg text-gray-600">{useCase.description}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 border border-orange-100"
            >
              <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A0A0A' }}>
                Why TypeIt4U for {useCase.audience}
              </h2>
              <ul className="space-y-4">
                {useCase.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)' }}
                    >
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Use Cases */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-white rounded-2xl p-8 border border-gray-200"
            >
              <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A0A0A' }}>
                Perfect For
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {useCase.useCases.map((item, i) => (
                  <div 
                    key={i} 
                    className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl p-4 text-center"
                  >
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Testimonial Placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mt-12 bg-white rounded-2xl p-8 border border-gray-200 text-center"
          >
            <div className="text-4xl mb-4">💬</div>
            <p className="text-xl italic text-gray-700 mb-4">
              "TypeIt4U has completely changed how I type on my phone. The AI features save me so much time!"
            </p>
            <p className="text-sm text-gray-500">— A happy {useCase.audience.toLowerCase()} user</p>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mt-12 text-center bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-10 border border-orange-100"
          >
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Ready to Type Smarter?
            </h3>
            <p className="text-gray-600 mb-6">
              Join thousands of {useCase.audience.toLowerCase()} using TypeIt4U every day.
            </p>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                borderRadius: '26px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              <Download className="w-5 h-5" />
              Download TypeIt4U Free
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UseCaseArticle;
